<template>
  <FloatingPopup
    :id="popupId"
    :model-value="show"
    title="Browse Home Assistant media"
    :width="760"
    :height="620"
    :minwidth="560"
    :minheight="420"
    content-class="p-4 overflow-hidden"
    @update:model-value="$emit('close')">
    <div class="flex h-full min-h-0 flex-col gap-3">
      <div class="flex flex-none items-center gap-2">
        <button
          class="rounded px-2 py-1 text-sm text-cyan-400 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="path.length === 0 || loading"
          @click="goBack">
          ← Back
        </button>
        <div class="min-w-0 flex-1 truncate text-sm text-slate-300">
          {{ breadcrumbTitle }}
        </div>
        <span class="flex-none text-xs text-slate-500">{{ entityId }}</span>
      </div>

      <div v-if="loading" class="flex flex-1 items-center justify-center text-slate-400">
        Loading media…
      </div>
      <div v-else-if="error" class="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
        <p class="text-red-400">{{ error }}</p>
        <form-button @click="loadCurrent">Retry</form-button>
      </div>
      <div v-else class="flex min-h-0 flex-1 flex-col gap-2">
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search media..."
          aria-label="Search media"
          class="w-full flex-none rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none placeholder:text-slate-500 focus:border-cyan-500"
          @keydown.esc="clearSearch" />

        <div v-if="!filteredItems.length" class="flex flex-1 items-center justify-center text-slate-500">
          {{ emptyMessage }}
        </div>
        <div v-else class="min-h-0 flex-1 overflow-y-auto rounded border border-slate-600/60">
          <div
            v-for="item in filteredItems"
            :key="itemKey(item)"
            class="flex items-center gap-3 border-b border-slate-700 px-3 py-2 last:border-b-0 hover:bg-slate-800/70">
            <img
              v-if="item.thumbnail"
              :src="item.thumbnail"
              :alt="item.title || ''"
              class="h-10 w-10 flex-none rounded object-cover"
              @error="$event.target.style.display = 'none'" />
            <div v-else class="flex h-10 w-10 flex-none items-center justify-center rounded bg-slate-800 text-slate-500">♫</div>
            <button class="min-w-0 flex-1 text-left" @click="openItem(item)">
              <div class="truncate text-sm text-slate-200">{{ item.title || item.media_content_id }}</div>
              <div class="truncate text-xs text-slate-500">
                {{ item.media_content_type || item.media_class || 'media' }}
              </div>
            </button>
            <button
              v-if="item.can_play"
              class="flex-none rounded bg-cyan-700 px-2 py-1 text-xs text-white hover:bg-cyan-600"
              @click="selectItem(item)">
              Select
            </button>
            <button
              v-if="item.can_expand"
              class="flex-none rounded px-2 py-1 text-xs text-cyan-400 hover:bg-slate-700"
              @click="openItem(item)">
              Open
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-none justify-end">
        <form-button class="w-24" @click="$emit('close')">Close</form-button>
      </div>
    </div>
  </FloatingPopup>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import FloatingPopup from '../../FloatingPopup.vue';
import { browseMedia } from '../../../services/homeAssistantService.js';
import {
  clearMediaSearchQuery,
  filterMediaItems,
  mediaBrowserEmptyMessage,
} from './mediaBrowser.js';

const props = defineProps({
  show:     { type: Boolean, required: true },
  settings: { type: Object, required: true },
  entityId: { type: String, default: '' },
});
const emit = defineEmits(['close', 'select']);
const popupId = `hass-media-browser-${props.entityId}-${Math.random().toString(36).slice(2)}`;

const loading = ref(false);
const error = ref('');
const current = ref(null);
const path = ref([]);
const searchQuery = ref('');

const items = computed(() => Array.isArray(current.value?.children) ? current.value.children : []);
const filteredItems = computed(() => filterMediaItems(items.value, searchQuery.value));
const emptyMessage = computed(() => mediaBrowserEmptyMessage(items.value, filteredItems.value));
const breadcrumbTitle = computed(() => [
  'Media',
  ...path.value.map(entry => entry.title).filter(Boolean),
  current.value?.title,
].filter(Boolean).join(' / '));

function itemKey(item) {
  return `${item.media_content_type ?? ''}|${item.media_content_id ?? ''}|${item.title ?? ''}`;
}

async function load(mediaContentId = null, mediaContentType = null) {
  if (!props.entityId) return;
  loading.value = true;
  error.value = '';
  try {
    current.value = await browseMedia(props.settings, props.entityId, {
      mediaContentId,
      mediaContentType,
    });
  } catch (e) {
    error.value = String(e);
  } finally {
    loading.value = false;
  }
}

function loadCurrent() {
  const item = path.value[path.value.length - 1];
  return load(item?.media_content_id ?? null, item?.media_content_type ?? null);
}

function openItem(item) {
  if (!item.can_expand) return;
  clearSearch();
  path.value.push(item);
  load(item.media_content_id, item.media_content_type);
}

function goBack() {
  if (!path.value.length) return;
  clearSearch();
  path.value.pop();
  loadCurrent();
}

function clearSearch() {
  clearMediaSearchQuery(searchQuery);
}

function navigationIds() {
  return [
    {},
    ...path.value.map(item => ({
      media_content_type: item.media_content_type,
      media_content_id: item.media_content_id,
    })),
  ];
}

function selectItem(item) {
  emit('select', { item, navigateIds: navigationIds() });
}

watch(
  () => [props.show, props.entityId],
  ([show]) => {
    if (!show) return;
    path.value = [];
    current.value = null;
    clearSearch();
    load();
  },
  { immediate: true },
);
</script>
